'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useClerk, useSignIn, useUser } from '@clerk/nextjs';
import { clearAuthSwitchCookie } from '@lib/auth/utils/auth-switch/clear-auth-switch-cookie';
import { setAuthSwitchCookie } from '@lib/auth/utils/auth-switch/set-auth-switch-cookie';
import { ClerkCredentialError } from '@lib/clerk/errors/clerk-credential-error';
import { isAlreadySignedInError } from '@lib/clerk/utils/is-already-signed-in-error';
import { resolveClerkErrorMessage } from '@lib/clerk/utils/resolve-clerk-error-message';
import { APP_ROUTES } from '@lib/constants/apis';
export interface SignInCredentials {
  email: string;
  password: string;
}
export interface UseClerkSignInResult {
  signInWithCredentials: (credentials: SignInCredentials, redirectTo?: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to handle Clerk email/password sign-in
 *
 * @returns Object containing:
 *  - signInWithCredentials: Function to sign in with email and password
 *  - isLoading: Whether sign-in is in progress
 *  - error: Error message if sign-in failed, null otherwise
 *
 * @example
 * ```tsx
 * const { signInWithCredentials, isLoading, error } = useClerkSignIn();
 *
 * const handleSubmit = async (values) => {
 *   await signInWithCredentials(
 *     { email: values.email, password: values.password },
 *     '/dashboard'
 *   );
 * };
 * ```
 */
export function useClerkSignIn(): UseClerkSignInResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const {
    signIn
  } = useSignIn();
  const {
    signOut
  } = useClerk();
  const {
    isSignedIn
  } = useUser();
  const signInWithCredentials = async (credentials: SignInCredentials, redirectTo?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    let restoreToken: string | null = null;
    let shouldKeepLoading = false;
    let signInError: string | null = null;
    const wasSignedIn = isSignedIn;
    try {
      // Set cookie to signal auth switch in progress (prevents middleware redirect)
      setAuthSwitchCookie();

      // Only sign out if already signed in
      if (wasSignedIn) {
        // Fetch restoration token before signing out
        const response = await fetch('/api/auth/signin-token', {
          method: 'POST'
        });
        if (response.ok) {
          const data = await response.json();
          restoreToken = data.token;
        }
        await signOut(() => {});
      }
      const {
        error: passwordError
      } = await signIn.password({
        identifier: credentials.email,
        password: credentials.password
      });
      if (passwordError) {
        // Cross-tab race: `wasSignedIn` was false at entry but
        // a session arrived during the network round-trip.
        // Drive the redirect here; Clerk's client store may
        // lag the server, so the page-level guard can't be
        // relied on to fire promptly.
        if (isAlreadySignedInError(passwordError)) {
          shouldKeepLoading = true;
          router.replace(redirectTo || APP_ROUTES.HOME);
          return;
        }
        signInError = resolveClerkErrorMessage(passwordError, {
          flow: 'sign-in',
          userInfo: {
            email: credentials.email
          },
          fallback: "We couldn't sign you in. Please check your details and try again, or reset your password."
        });
        // Expected credential denial (unknown email, wrong password,
        // strategy mismatch). Shown inline — do not page Sentry.
        throw new ClerkCredentialError(signInError);
      }
      if (signIn.status === 'complete') {
        const {
          error: finalizeError
        } = await signIn.finalize();
        if (finalizeError) {
          signInError = finalizeError.message;
          throw new Error('Session activation failed');
        }
        if (redirectTo) {
          router.push(redirectTo);
          shouldKeepLoading = true; // Keep loading during redirect
        }
      } else if (signIn.status === 'needs_second_factor') {
        signInError = 'Two-factor authentication is required. Please use a different sign-in method.';
        throw new ClerkCredentialError('Two-factor authentication required');
      } else {
        signInError = `Sign in incomplete: ${signIn.status}. Please try again.`;
        throw new ClerkCredentialError(`Sign in incomplete: ${signIn.status}`);
      }
    } catch (err: unknown) {
      // Restore previous session if sign-in failed and we were signed in.
      // Best-effort: any thrown error (network, unexpected) must not
      // mask the original sign-in error from reaching the user.
      if (wasSignedIn && restoreToken) {
        try {
          const {
            error: restoreError
          } = await signIn.create({
            strategy: 'ticket',
            ticket: restoreToken
          });
          if (!restoreError && signIn.status === 'complete') {
            await signIn.finalize();
          }
        } catch {
          // Restoration failed → user remains signed out
        }
      }
      const message = signInError || "We couldn't sign you in. Please check your details and try again, or reset your password.";
      setError(message);
      // Re-throw so the caller can react. Prefer the curated
      // message over the placeholder Error used for control flow.
      throw err instanceof Error && err.message ? err : new Error(message);
    } finally {
      clearAuthSwitchCookie();
      if (!shouldKeepLoading) {
        setIsLoading(false);
      }
    }
  };
  return {
    signInWithCredentials,
    isLoading,
    error
  };
}